import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Check,
  CheckCircle2,
  ChevronLeft,
  Image as ImageIcon,
  Loader2,
  Pencil,
  Search,
  Ship,
  Trash2,
  Upload,
  XCircle,
} from 'lucide-react';
import { uploadsApi } from '@/api/endpoints/uploads';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { devicesApi } from '@/api/endpoints/devices';
import { useFleetStore } from '@/stores/fleet';
import { cn } from '@/lib/utils';
import {
  VESSEL_CATEGORIES,
  ICON_COLORS,
  DEFAULT_ICON_COLOR,
  getDeviceIconColor,
} from '@/lib/vesselCategories';
import { VesselIcon } from '@/components/device/vesselIcons';
import type { Device } from '@/api/types';
import { mensagemDeErro } from '@/lib/apiError';

const CATEGORIES: { value: string; label: string }[] = [
  { value: '', label: '— Selecione —' },
  ...VESSEL_CATEGORIES,
];

interface FormState {
  name: string;
  uniqueId: string;
  model: string;
  category: string;
  iconColor: string;
  phone: string;
  apelido: string;
  photo: string;
  // Equipamento & chip (vão em attributes — Traccar padrão)
  statusEquip: string;
  vendor: string;
  fornecedor: string;
  hardware: string;
  imeiequipamento: string;
  chassi: string;
  icc: string;
  operator: string;
  dateInstall: string;
  localInstall: string;
  tech: string;
  infoinstalacao: string;
  // Documentos da embarcação (atributos)
  vesselType: string;
  registro: string;
  casco: string;
  motor: string;
  potencia: string;
  proprietario: string;
  seguradora: string;
  apolice: string;
  marina: string;
  vencSeguro: string;
  vencDoc: string;
}

const EMPTY: FormState = {
  name: '',
  uniqueId: '',
  model: '',
  category: '',
  iconColor: '',
  phone: '',
  apelido: '',
  photo: '',
  statusEquip: '',
  vendor: '',
  fornecedor: '',
  hardware: '',
  imeiequipamento: '',
  chassi: '',
  icc: '',
  operator: '',
  dateInstall: '',
  localInstall: '',
  tech: '',
  infoinstalacao: '',
  vesselType: '',
  registro: '',
  casco: '',
  motor: '',
  potencia: '',
  proprietario: '',
  seguradora: '',
  apolice: '',
  marina: '',
  vencSeguro: '',
  vencDoc: '',
};

/** Status de inventário do equipamento (rastreador). */
export const EQUIP_STATUS: { value: string; label: string }[] = [
  { value: '', label: '— Selecione —' },
  { value: 'estoque', label: 'Em estoque' },
  { value: 'instalado', label: 'Instalado' },
  { value: 'manutencao', label: 'Em manutenção' },
  { value: 'rma', label: 'RMA / garantia' },
  { value: 'baixado', label: 'Baixado' },
];

const VESSEL_TYPES = [
  '', 'Barco', 'Casco', 'BassBoat', 'Bote', 'Apoio', 'Canoa', 'Caiaque', 'Lancha',
  'Reboque', 'Veleiro', 'Catamarã', 'Iate', 'Escuna', 'Ferry boat', 'Balsa',
  'Resgate', 'Plataformas', 'Flutuante', 'Jet ski', 'Outro',
];

function fromDevice(device: Device): FormState {
  const a = device.attributes ?? {};
  const str = (k: string) => (typeof a[k] === 'string' ? (a[k] as string) : '');
  // Campo que pode vir top-level (coluna TrackMax) OU em attributes.
  const top = (k: keyof Device) =>
    (typeof device[k] === 'string' ? (device[k] as string) : '') || str(k as string);
  return {
    name: device.name ?? '',
    uniqueId: device.uniqueId ?? '',
    model: device.model ?? '',
    category: device.category ?? '',
    iconColor: getDeviceIconColor(a) ?? '',
    phone: device.phone ?? '',
    apelido: str('apelido') || str('nickname') || device.contact || '',
    photo: device.photo || str('photo') || str('image') || '',
    statusEquip: str('statusEquip'),
    vendor: top('vendor'),
    fornecedor: str('fornecedor'),
    hardware: top('hardware'),
    imeiequipamento: top('imeiequipamento') || device.uniqueId || '',
    chassi: top('chassi'),
    icc: top('icc'),
    operator: top('operator'),
    dateInstall: top('dateInstall'),
    localInstall: top('localInstall'),
    tech: top('tech'),
    infoinstalacao: top('infoinstalacao'),
    vesselType: str('vesselType'),
    registro: str('registro'),
    casco: str('casco'),
    motor: str('motor'),
    potencia: str('potencia'),
    proprietario: str('proprietario'),
    seguradora: str('seguradora'),
    apolice: str('apolice'),
    marina: str('marina'),
    vencSeguro: str('vencSeguro'),
    vencDoc: str('vencDoc'),
  };
}

export function AddDevicePage() {
  const params = useParams<{ id?: string }>();
  const [sp] = useSearchParams();
  const editId = params.id ? Number(params.id) : null;
  const isEdit = editId !== null && Number.isFinite(editId);
  // Vínculo opcional a um grupo/cliente (fluxo "Novo dispositivo" da revenda).
  const presetGroupId = sp.get('group') ? Number(sp.get('group')) : null;
  const presetClient = sp.get('client');
  // Origem = lista "Embarcações cadastradas" (na própria tela de adicionar) → volta pra lá.
  const fromAdd = sp.get('from') === 'add';
  const backTo = isEdit
    ? fromAdd
      ? '/settings/devices/new'
      : `/devices/${editId}`
    : '/settings';

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const existingDevice = useFleetStore((s) => (isEdit ? s.devices.get(editId!) : undefined));
  const [form, setForm] = useState<FormState>(EMPTY);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (isEdit && existingDevice) {
      setForm(fromDevice(existingDevice));
    }
  }, [isEdit, existingDevice]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  // Upload de foto da embarcação
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const [imgErr, setImgErr] = useState(false);
  // Popup de resultado do salvamento
  const [saveResult, setSaveResult] = useState<{ ok: boolean; msg: string; dest: string | null } | null>(null);

  const photoPreview = (() => {
    const v = form.photo.trim();
    if (!v) return '';
    if (/^https?:\/\//i.test(v) || v.startsWith('/')) return v;
    return `/photos/devices/${encodeURIComponent(v)}`;
  })();
  useEffect(() => setImgErr(false), [photoPreview]);

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setUploadErr(null);
    setUploading(true);
    try {
      const res = await uploadsApi.devicePhoto(f);
      update('photo', res.url);
    } catch (err) {
      setUploadErr((err as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const errors = useMemo(() => {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) e.name = 'Obrigatório';
    if (!form.uniqueId.trim()) e.uniqueId = 'Obrigatório';
    return e;
  }, [form]);

  const valid = Object.keys(errors).length === 0;

  const buildPayload = (): Device => {
    const baseAttrs = existingDevice?.attributes ?? {};
    const t = (v: string) => v.trim() || undefined;
    const attributes = {
      ...baseAttrs,
      iconColor: form.iconColor || undefined,
      apelido: t(form.apelido),
      statusEquip: form.statusEquip || undefined,
      // Foto + equipamento/chip: o Traccar padrão NÃO tem essas colunas top-level
      // (rejeita com 400). Guardamos em attributes; a leitura já usa esse fallback.
      photo: t(form.photo),
      vendor: t(form.vendor),
      fornecedor: t(form.fornecedor),
      hardware: t(form.hardware),
      imeiequipamento: t(form.imeiequipamento),
      chassi: t(form.chassi),
      icc: t(form.icc),
      operator: t(form.operator),
      dateInstall: t(form.dateInstall),
      localInstall: t(form.localInstall),
      tech: t(form.tech),
      infoinstalacao: t(form.infoinstalacao),
      // Documentos da embarcação
      vesselType: t(form.vesselType),
      registro: t(form.registro),
      casco: t(form.casco),
      motor: t(form.motor),
      potencia: t(form.potencia),
      proprietario: t(form.proprietario),
      seguradora: t(form.seguradora),
      apolice: t(form.apolice),
      marina: t(form.marina),
      vencSeguro: t(form.vencSeguro),
      vencDoc: t(form.vencDoc),
    };
    return {
      ...(existingDevice ?? { id: 0 }),
      // No fluxo da revenda, já cadastra a embarcação no grupo do cliente.
      ...(!isEdit && presetGroupId ? { groupId: presetGroupId } : {}),
      name: form.name.trim(),
      uniqueId: form.uniqueId.trim(),
      model: form.model.trim() || undefined,
      category: form.category || undefined,
      phone: form.phone.trim() || undefined,
      contact: form.apelido.trim() || undefined,
      attributes,
    } as Device;
  };

  const errorText = mensagemDeErro;

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = buildPayload();
      if (isEdit && existingDevice) {
        return devicesApi.update(existingDevice.id, payload);
      }
      return devicesApi.create(payload);
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['devices'] });
      const id = (saved as Device | undefined)?.id ?? existingDevice?.id;
      const dest = fromAdd ? '/settings/devices/new' : id ? `/devices/${id}` : '/devices';
      setSaveResult({
        ok: true,
        msg: isEdit ? 'Embarcação atualizada com sucesso.' : 'Embarcação cadastrada com sucesso.',
        dest,
      });
    },
    onError: (e) => setSaveResult({ ok: false, msg: errorText(e), dest: null }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!existingDevice) throw new Error('Sem embarcação pra excluir');
      return devicesApi.delete(existingDevice.id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['devices'] });
      navigate('/devices');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid) return;
    saveMutation.mutate();
  };

  const onConfirmDelete = () => {
    if (!existingDevice) return;
    if (!window.confirm(`Excluir a embarcação "${existingDevice.name}"? Essa ação não pode ser desfeita.`)) return;
    deleteMutation.mutate();
  };

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4 sm:p-6">
    <form onSubmit={handleSubmit} className="space-y-4">
      <Link
        to={backTo}
        className="-ml-2 inline-flex items-center gap-0.5 rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-primary/5"
      >
        <ChevronLeft className="h-5 w-5" />
        Voltar
      </Link>

      <div>
        <h1 className="text-xl font-bold">
          {isEdit ? 'Editar embarcação' : 'Adicionar embarcação'}
        </h1>
        <p className="text-sm text-muted-foreground">
          {isEdit
            ? 'Atualize os dados cadastrais. O ID único (IMEI) só deve ser alterado em caso de troca do rastreador.'
            : 'Cadastre uma nova embarcação. O ID único é o IMEI ou identificador serial do rastreador.'}
        </p>
        {!isEdit && presetClient && (
          <p className="mt-1 inline-block rounded-md bg-primary/10 px-2 py-1 text-xs font-semibold text-primary">
            Será cadastrada no cliente: {presetClient}
          </p>
        )}
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <Field label="Nome / Registro" required error={touched ? errors.name : undefined}>
            <Input
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="Ex: STRADA-IWI-6934 ou A05H13P2120"
              autoFocus={!isEdit}
            />
          </Field>
          <Field label="ID único (IMEI)" required error={touched ? errors.uniqueId : undefined}>
            <Input
              value={form.uniqueId}
              onChange={(e) => update('uniqueId', e.target.value)}
              placeholder="Ex: 354778341714494"
              disabled={isEdit}
            />
            {isEdit && (
              <p className="mt-1 text-[10px] text-muted-foreground">
                ID único não é editável depois do cadastro.
              </p>
            )}
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Modelo">
              <Input
                value={form.model}
                onChange={(e) => update('model', e.target.value)}
                placeholder="CRIOPAD FULL, LUMII PRO..."
              />
            </Field>
            <Field label="Categoria">
              <select
                value={form.category}
                onChange={(e) => update('category', e.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Cor do ícone">
            <div className="flex items-center gap-3">
              <span
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-white ring-2 ring-white shadow"
                style={{ background: form.iconColor || DEFAULT_ICON_COLOR }}
                title="Pré-visualização no mapa"
              >
                <VesselIcon category={form.category} className="h-7 w-7" />
              </span>
              <div className="flex flex-wrap gap-1.5">
                {ICON_COLORS.map((c) => {
                  const active = (form.iconColor || DEFAULT_ICON_COLOR) === c.value;
                  return (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => update('iconColor', c.value)}
                      title={c.label}
                      aria-label={c.label}
                      className={cn(
                        'h-7 w-7 rounded-full ring-2 transition-transform hover:scale-110',
                        active ? 'ring-foreground' : 'ring-transparent',
                      )}
                      style={{ background: c.value }}
                    />
                  );
                })}
              </div>
            </div>
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Linha (número do SIM)">
              <Input
                value={form.phone}
                onChange={(e) => update('phone', e.target.value)}
                placeholder="Ex: 5511935621212"
              />
            </Field>
            <Field label="Apelido">
              <Input
                value={form.apelido}
                onChange={(e) => update('apelido', e.target.value)}
                placeholder="Nome amigável"
              />
            </Field>
          </div>

          <Field label="Foto da embarcação">
            <div className="flex items-start gap-3">
              {photoPreview && !imgErr ? (
                <img
                  src={photoPreview}
                  alt="Pré-visualização"
                  className="h-16 w-16 shrink-0 rounded-lg object-cover ring-1 ring-border"
                  onError={() => setImgErr(true)}
                />
              ) : (
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-secondary text-muted-foreground">
                  <ImageIcon className="h-6 w-6" />
                </div>
              )}
              <div className="min-w-0 flex-1 space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={onPickFile}
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="gap-1.5"
                  >
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {uploading ? 'Enviando…' : 'Enviar imagem'}
                  </Button>
                  {form.photo && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => update('photo', '')}>
                      Remover
                    </Button>
                  )}
                </div>
                <Input
                  value={form.photo}
                  onChange={(e) => update('photo', e.target.value)}
                  placeholder="ou cole um nome de arquivo / URL"
                />
                {uploadErr && (
                  <p className="text-[11px] font-semibold text-status-blocked">{uploadErr}</p>
                )}
                <p className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <ImageIcon className="h-3 w-3" />
                  Faça upload (JPG/PNG/WEBP/GIF, máx. 10 MB) ou informe nome/URL. Nome só → /photos/devices/&lt;arquivo&gt;.
                </p>
              </div>
            </div>
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-4">
          <h2 className="text-sm font-bold">Equipamento &amp; chip</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Status do equipamento">
              <select
                value={form.statusEquip}
                onChange={(e) => update('statusEquip', e.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {EQUIP_STATUS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Fabricante (vendor)">
              <Input value={form.vendor} onChange={(e) => update('vendor', e.target.value)} placeholder="Ex: TRACKMAX" />
            </Field>
            <Field label="Fornecedor">
              <Input value={form.fornecedor} onChange={(e) => update('fornecedor', e.target.value)} placeholder="Quem forneceu o chip/equipamento" />
            </Field>
            <Field label="Modelo do equipamento (hardware)">
              <Input value={form.hardware} onChange={(e) => update('hardware', e.target.value)} placeholder="Ex: J16, CRX" />
            </Field>
            <Field label="IMEI do equipamento">
              <Input value={form.imeiequipamento} onChange={(e) => update('imeiequipamento', e.target.value)} placeholder="Ex: 869731052532447" />
            </Field>
            <Field label="ICCID (chip)">
              <Input value={form.icc} onChange={(e) => update('icc', e.target.value)} placeholder="8988303000010526..." />
            </Field>
            <Field label="Operadora">
              <Input value={form.operator} onChange={(e) => update('operator', e.target.value)} placeholder="VIVO, CHIP MAX…" />
            </Field>
            <Field label="Chassi">
              <Input value={form.chassi} onChange={(e) => update('chassi', e.target.value)} />
            </Field>
            <Field label="Data de instalação">
              <Input value={form.dateInstall} onChange={(e) => update('dateInstall', e.target.value)} placeholder="dd/mm/aaaa" />
            </Field>
            <Field label="Local de instalação">
              <Input value={form.localInstall} onChange={(e) => update('localInstall', e.target.value)} />
            </Field>
            <Field label="Técnico">
              <Input value={form.tech} onChange={(e) => update('tech', e.target.value)} />
            </Field>
          </div>
          <Field label="Informações da instalação">
            <Input value={form.infoinstalacao} onChange={(e) => update('infoinstalacao', e.target.value)} placeholder="Observações da instalação" />
          </Field>
          <p className="text-[10px] text-muted-foreground">
            ICCID/operadora aqui refletem o chip instalado. A gestão completa de chips (estoque, ciclo de vida) fica em <strong>Chips / SIM</strong>.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-4">
          <h2 className="text-sm font-bold">Documentos da embarcação</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Tipo de embarcação">
              <select
                value={form.vesselType}
                onChange={(e) => update('vesselType', e.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {VESSEL_TYPES.map((v) => (
                  <option key={v} value={v}>{v || '—'}</option>
                ))}
              </select>
            </Field>
            <Field label="Registro / Matrícula">
              <Input value={form.registro} onChange={(e) => update('registro', e.target.value)} placeholder="Ex: RJ-2841" />
            </Field>
            <Field label="Nº do casco">
              <Input value={form.casco} onChange={(e) => update('casco', e.target.value)} />
            </Field>
            <Field label="Motor">
              <Input value={form.motor} onChange={(e) => update('motor', e.target.value)} placeholder="Ex: Mercury 250" />
            </Field>
            <Field label="Potência">
              <Input value={form.potencia} onChange={(e) => update('potencia', e.target.value)} placeholder="Ex: 250 HP" />
            </Field>
            <Field label="Proprietário">
              <Input value={form.proprietario} onChange={(e) => update('proprietario', e.target.value)} />
            </Field>
            <Field label="Seguradora">
              <Input value={form.seguradora} onChange={(e) => update('seguradora', e.target.value)} />
            </Field>
            <Field label="Apólice">
              <Input value={form.apolice} onChange={(e) => update('apolice', e.target.value)} />
            </Field>
            <Field label="Marina">
              <Input value={form.marina} onChange={(e) => update('marina', e.target.value)} />
            </Field>
            <Field label="Venc. do seguro">
              <Input type="date" value={form.vencSeguro} onChange={(e) => update('vencSeguro', e.target.value)} />
            </Field>
            <Field label="Venc. do documento">
              <Input type="date" value={form.vencDoc} onChange={(e) => update('vencDoc', e.target.value)} />
            </Field>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Vencimentos aparecem no detalhe da embarcação; lembretes automáticos via módulo de manutenção (V2).
          </p>
        </CardContent>
      </Card>

      {deleteMutation.error && (
        <p className="flex items-start gap-2 rounded-md bg-status-blocked/10 px-3 py-2 text-xs font-medium text-status-blocked">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {errorText(deleteMutation.error)}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        {isEdit && existingDevice && (
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirmDelete}
            disabled={deleteMutation.isPending || saveMutation.isPending}
            className="mr-auto gap-1.5"
          >
            <Trash2 className="h-4 w-4" />
            {deleteMutation.isPending ? 'Excluindo…' : 'Excluir'}
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate(backTo)}
        >
          Cancelar
        </Button>
        <Button type="submit" disabled={!valid || saveMutation.isPending} className="gap-1.5">
          <Check className="h-4 w-4" />
          {saveMutation.isPending
            ? 'Salvando…'
            : isEdit
              ? 'Salvar alterações'
              : 'Criar embarcação'}
        </Button>
      </div>
    </form>

      {!isEdit && <RegisteredVessels />}

      {saveResult && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 p-4"
          onClick={() => setSaveResult(null)}
        >
          <div
            className="w-full max-w-sm rounded-xl bg-card p-5 text-center shadow-xl ring-1 ring-border"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="flex flex-col items-center gap-2">
              {saveResult.ok ? (
                <CheckCircle2 className="h-12 w-12 text-status-running" />
              ) : (
                <XCircle className="h-12 w-12 text-status-blocked" />
              )}
              <h2 className="text-base font-bold">
                {saveResult.ok ? 'Tudo certo!' : 'Não foi possível salvar'}
              </h2>
              <p className="text-sm text-muted-foreground">{saveResult.msg}</p>
            </div>
            <div className="mt-5 flex justify-center">
              <Button
                onClick={() => {
                  const dest = saveResult.dest;
                  setSaveResult(null);
                  if (dest) navigate(dest);
                }}
                className="min-w-[120px]"
              >
                OK
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Lista das embarcações já cadastradas (na tela de "Adicionar"), com busca e atalho de edição. */
function RegisteredVessels() {
  const devices = useFleetStore((s) => s.devices);
  const [q, setQ] = useState('');
  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    const arr = Array.from(devices.values());
    const filtered = term
      ? arr.filter(
          (d) =>
            d.name?.toLowerCase().includes(term) ||
            d.uniqueId?.toLowerCase().includes(term) ||
            d.model?.toLowerCase().includes(term),
        )
      : arr;
    return filtered.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'pt-BR'));
  }, [devices, q]);

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <h2 className="flex items-center gap-2 text-sm font-bold">
          <Ship className="h-4 w-4 text-primary" />
          Embarcações cadastradas ({devices.size})
        </h2>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome, IMEI ou modelo"
            className="rounded-full bg-secondary pl-9"
          />
        </div>
        {list.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {devices.size === 0 ? 'Nenhuma embarcação cadastrada ainda.' : 'Nenhuma corresponde à busca.'}
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {list.map((d) => (
              <li key={d.id}>
                <Link
                  to={`/settings/devices/edit/${d.id}?from=add`}
                  className="flex items-center gap-3 rounded-md px-1 py-2.5 hover:bg-accent"
                >
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white"
                    style={{ background: getDeviceIconColor(d.attributes ?? {}) || DEFAULT_ICON_COLOR }}
                  >
                    <VesselIcon category={d.category} className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{d.name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {d.uniqueId}
                      {d.model ? ` · ${d.model}` : ''}
                    </p>
                  </div>
                  <Pencil className="h-4 w-4 shrink-0 text-muted-foreground" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
        <span>
          {label}
          {required && <span className="text-status-blocked"> *</span>}
        </span>
        {error && <span className="text-status-blocked">{error}</span>}
      </span>
      <div className={cn(error && 'rounded-md ring-2 ring-status-blocked/30')}>{children}</div>
    </label>
  );
}
